import { Table } from '@radix-ui/themes'
import { css, keyframes, styled } from 'styled-components'

const pageFadeIn = keyframes`
  from { opacity: 0.5; }
  to { opacity: 1; }
`

export const StyledTableRoot = styled(Table.Root)<{
  $cellPadding?: string
  $cellFontSize?: string
  $borderSpacing?: string
  $fixedLayout?: boolean
}>`
  height: 100%;
  ${({ $cellPadding }) =>
    $cellPadding ? `--table-cell-padding: ${$cellPadding};` : ''}
  ${({ $cellFontSize }) =>
    $cellFontSize ? `--table-cell-font-size: ${$cellFontSize};` : ''}

  table {
    border-collapse: separate;
    border-spacing: ${({ $borderSpacing }) => $borderSpacing || '0 1px'};
    ${({ $fixedLayout }) => ($fixedLayout ? 'table-layout: fixed;' : '')}
  }
`

// When maxHeight is provided, we need to make rt-ScrollAreaRoot scrollable
export const StyledTableRootWithMaxHeight = styled(StyledTableRoot)`
  /* Target the rt-ScrollAreaRoot created by Radix Table.Root */
  .rt-ScrollAreaRoot {
    overflow-y: auto;
    overflow-x: auto;
    max-height: var(--table-max-height);
  }
`

export const TableContainer = styled.div<{
  $maxHeight?: string
}>`
  overflow: ${({ $maxHeight }) => ($maxHeight ? 'visible' : 'auto')};
  flex-grow: 1;
  flex: 1;
  position: relative;
  ${({ $maxHeight }) =>
    $maxHeight ? `--table-max-height: ${$maxHeight};` : ''}
`
export const TopBarContainer = styled.div`
  padding: 0 0.5rem;
`
export const StyledSubheaderSection = styled.div`
  padding-bottom: 0.5rem;
`

interface StyledTableHeaderProps {
  $isTableHeaderSticky?: boolean
}

export const StyledTableHeader = styled(Table.Header)<StyledTableHeaderProps>`
  color: ${({ theme }) => theme.forecast.defaultText};
  height: 2.5rem;
  flex-grow: 0;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.forecast.pageBackground1};

  ${({ $isTableHeaderSticky }) =>
    $isTableHeaderSticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 2;

      /* Ensure header cells are also sticky */
      th {
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: ${({ theme }) => theme.forecast.pageBackground1};
      }
    `}
`

interface StyledTableCellProps {
  $cellPadding?: string
  $cellFontSize?: string
}

export const StyledTableCell = styled(Table.Cell)<StyledTableCellProps>`
  color: ${({ theme }) => theme.forecast.defaultText};
  padding: ${({ $cellPadding }) => $cellPadding || '0.5rem'};
  text-align: left;
  vertical-align: middle;
  min-height: 3.5rem;
  border-style: hidden !important;
  white-space: nowrap;
  font-size: ${({ $cellFontSize }) => $cellFontSize || 'inherit'};
`

export const StyledTableBody = styled(Table.Body)`
  animation: ${pageFadeIn} 150ms ease-out;
`

export const StyledTableRow = styled(Table.Row)<{ $hasOnClick: boolean }>`
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  position: relative;

  ${({ $hasOnClick }) =>
    $hasOnClick &&
    css`
      &:hover {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }

        &:hover * {
          cursor: pointer;
        }
      }

      &:active {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }
      }
    `}
`

export const StyledPaginationContainer = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  display: flex;
  flex-direction: row;
  height: 56px;
  justify-content: space-between;
  align-items: center;
  bottom: 0;
  width: 100%;
  padding: 0 0.25rem;
  flex-grow: 0;
  flex-shrink: 0;

  footer {
    flex-shrink: 0;
  }
`
